import React, { useState } from 'react'
import {
  ArrayInput,
  FormDataConsumer,
  InputProps,
  SimpleFormIterator,
} from 'react-admin'
import { getById } from 'services/utils'
import { FormDataConsumerProps, WithGetSource, WithId } from 'types/common'
import LocalizedSelectArrayInput from 'ui-shared/LocalizedFields/LocalizedSelectArrayInput'
import LocalizedSelectInput from 'ui-shared/LocalizedFields/LocalizedSelectInput'
import { WithSelectedProduct } from './types'
import { OptionedPackage } from 'services/api/entities/catalog/packages/common'
import { useFormContext } from 'react-hook-form'
import UpsellingPresetSelector from './UpsellingPresetSelector'
import QuantityMultiplier from './QuantityMultiplier'
import { FieldPath } from 'react-hook-form'
import { OptionedProduct } from 'services/api/entities/catalog/optionedProduct/types/internal/optionedProduct-models'
import { Food } from 'services/api/entities/catalog/food/types/internal/food-models'
import { Experience } from 'services/api/entities/catalog/experience/types/internal/experience-models'

export type OptionedProductPresetFieldsProps = Partial<
  WithSelectedProduct<Food | Experience> & WithGetSource & InputProps
>

export const OptionedProductPresetFields: React.FC<
  OptionedProductPresetFieldsProps
> = ({ getSource, selectedProduct }) => {
  const [cachedSelectedProduct, setCachedSelectedProduct] =
    useState(selectedProduct)

  const { setValue } = useFormContext<OptionedPackage>()

  if (!selectedProduct) return null

  // if the product is changed we reset the options with the default ones of the new product
  if (cachedSelectedProduct?.id !== selectedProduct.id) {
    setValue(
      getSource!('options') as FieldPath<OptionedPackage>,
      getDefaultOptionGroup(selectedProduct)
    )
    setCachedSelectedProduct(selectedProduct)
  }

  return (
    <>
      <QuantityMultiplier getSource={getSource} />

      {/* Options */}
      {!!selectedProduct.options.length && (
        <ArrayInput label="form.fields.options" source={getSource!('options')}>
          <SimpleFormIterator disableAdd disableRemove>
            <LocalizedSelectInput
              source="id"
              choices={selectedProduct.options}
              fullWidth
              label="form.fields.optionGroup"
              forceDisabled
            />
            <FormDataConsumer>
              {({
                scopedFormData,
                getSource: gs,
              }: FormDataConsumerProps<unknown, WithId>) => (
                <LocalizedSelectArrayInput
                  label="form.fields.option"
                  source={gs!('items')}
                  choices={
                    getById(selectedProduct.options, scopedFormData?.id!)?.items
                  }
                  fullWidth
                />
              )}
            </FormDataConsumer>
          </SimpleFormIterator>
        </ArrayInput>
      )}

      {/* Upsellings */}
      {!!selectedProduct.upsellingItemsFull.length && (
        <ArrayInput
          source={getSource!('upselling')}
          label="form.fields.upsellingItems"
        >
          <SimpleFormIterator>
            <UpsellingPresetSelector selectedProduct={selectedProduct} />
          </SimpleFormIterator>
        </ArrayInput>
      )}
    </>
  )
}

const getDefaultOptionGroup = (selectedProduct?: OptionedProduct) =>
  selectedProduct?.options.map((g) => ({
    id: g.id,
    items: [],
  }))
